// Edge endpoint anchors and routing, matching the canvas exactly. Anchors sit
// where ReactFlow places the handles: cardinal handles at the side midpoints of
// the node box (CustomNode), bus handles grid-spaced along the bus (BusNode via
// busLayout). Routing reuses ReactFlow's own getSmoothStepPath for edges
// without waypoints, so the exported polyline is the same step path the canvas
// draws — never a straight diagonal.

import { Position, getSmoothStepPath } from 'reactflow';
import { getBusLayout, parseBusHandleIndex } from '../../busLayout';
import { flattenPathData, dedupePoints } from './svgPath';

const KNOWN_HANDLES = new Set(['top', 'bottom', 'left', 'right']);
const DEFAULT_NODE_SIZE = 100;
const isBusHandleId = (handle) => /^(handle|target)-\d+$/.test(String(handle ?? ''));

/** The node's rendered box — same width/height defaulting as mapDocument.toFlowNode. */
export function nodeBox(node) {
  return {
    x: node.position?.x || 0,
    y: node.position?.y || 0,
    width: typeof node.width === 'number' ? node.width : DEFAULT_NODE_SIZE,
    height: typeof node.height === 'number' ? node.height : DEFAULT_NODE_SIZE,
  };
}

/**
 * Resolve a handle id to its anchor point in flow coordinates. Mirrors
 * mapDocument.toFlowEdge's normalization: unknown ids fall back to the
 * cardinal default ('bottom' for sources, 'top' for targets). Returns null
 * when the referenced handle does not exist on this node type (a bus handle
 * on a non-bus node) — the canvas does not render such an edge either.
 */
export function edgeAnchor(node, handleId, role) {
  const box = nodeBox(node);
  const fallback = role === 'source' ? 'bottom' : 'top';
  const handle = handleId && (KNOWN_HANDLES.has(handleId) || isBusHandleId(handleId)) ? handleId : fallback;

  if (isBusHandleId(handle)) {
    if ((node.type || 'customNode') !== 'busNode') return null;
    const layout = getBusLayout(node);
    const index = parseBusHandleIndex(handle, layout.handleCount);
    const axisPos = layout.positions[index] ?? index * layout.gridGap;
    // target-N sits on top of the bar, handle-N below it (BusNode.tsx).
    if (layout.horizontal) {
      return handle.startsWith('target-')
        ? { x: box.x + axisPos, y: box.y, position: Position.Top }
        : { x: box.x + axisPos, y: box.y + box.height, position: Position.Bottom };
    }
    return handle.startsWith('target-')
      ? { x: box.x, y: box.y + axisPos, position: Position.Left }
      : { x: box.x + box.width, y: box.y + axisPos, position: Position.Right };
  }

  switch (handle) {
    case 'top':
      return { x: box.x + box.width / 2, y: box.y, position: Position.Top };
    case 'bottom':
      return { x: box.x + box.width / 2, y: box.y + box.height, position: Position.Bottom };
    case 'left':
      return { x: box.x, y: box.y + box.height / 2, position: Position.Left };
    case 'right':
      return { x: box.x + box.width, y: box.y + box.height / 2, position: Position.Right };
    default:
      return null;
  }
}

/**
 * The polyline an edge renders as, in flow coordinates. With waypoints:
 * straight segments source → w0 → … → target (OrthogonalEdge). Without:
 * ReactFlow's smooth-step path at borderRadius 0, flattened to its vertices.
 */
export function edgeRoutePoints(source, target, waypoints) {
  if (Array.isArray(waypoints) && waypoints.length > 0) {
    return dedupePoints([
      { x: source.x, y: source.y },
      ...waypoints.map((w) => ({ x: w?.x || 0, y: w?.y || 0 })),
      { x: target.x, y: target.y },
    ]);
  }
  const [path] = getSmoothStepPath({
    sourceX: source.x,
    sourceY: source.y,
    targetX: target.x,
    targetY: target.y,
    sourcePosition: source.position,
    targetPosition: target.position,
    borderRadius: 0,
  });
  // borderRadius 0 yields only line segments (plus degenerate zero-size bends);
  // flattening through the shared path parser collapses those to vertices.
  const subPaths = flattenPathData(path);
  return dedupePoints(subPaths.flatMap((sp) => sp.points));
}
